/**
 * graphExport.js - Export-Utilities für Netzwerk-Graphen
 * Unterstützt PNG (aus Canvas) und SVG (aus Graph-Daten) Export.
 */

/**
 * Exportiert den Graph als PNG-Bild aus dem Canvas-Element.
 * @param {HTMLElement} containerEl - Container-Element das den Canvas enthält
 * @param {string} filename - Dateiname ohne Extension
 */
export function exportGraphAsPNG(containerEl, filename = 'netzwerk-graph') {
  const canvas = containerEl?.querySelector('canvas');
  if (!canvas) {
    console.error('Canvas-Element nicht gefunden');
    return false;
  }

  // Neues Canvas mit weißem Hintergrund erstellen
  const exportCanvas = document.createElement('canvas');
  exportCanvas.width = canvas.width;
  exportCanvas.height = canvas.height;
  const ctx = exportCanvas.getContext('2d');

  // Weißer Hintergrund
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

  // Originales Canvas darauf zeichnen
  ctx.drawImage(canvas, 0, 0);

  // Wasserzeichen
  ctx.fillStyle = '#94a3b8';
  ctx.font = '11px Inter, system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText(`Olympus Betreuungssoftware · ${new Date().toLocaleDateString('de-DE')}`, exportCanvas.width - 12, exportCanvas.height - 10);

  // Download
  const link = document.createElement('a');
  link.download = `${filename}_${new Date().toISOString().slice(0, 10)}.png`;
  link.href = exportCanvas.toDataURL('image/png');
  link.click();
  return true;
}

/**
 * Exportiert den Graph als SVG aus den Graph-Daten.
 * @param {Object} graphData - { nodes: [...], links: [...] }
 * @param {Object} options - { width, height, title, filename }
 */
export function exportGraphAsSVG(graphData, options = {}) {
  const {
    width = 800,
    height = 600,
    title = 'Netzwerk-Graph',
    filename = 'netzwerk-graph'
  } = options;

  if (!graphData?.nodes?.length) {
    console.error('Keine Graph-Daten vorhanden');
    return false;
  }

  // Bounding Box berechnen
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const node of graphData.nodes) {
    if (node.x == null || node.y == null) continue;
    minX = Math.min(minX, node.x);
    maxX = Math.max(maxX, node.x);
    minY = Math.min(minY, node.y);
    maxY = Math.max(maxY, node.y);
  }

  // Padding
  const pad = 60;
  const dataW = maxX - minX || 1;
  const dataH = maxY - minY || 1;
  const scale = Math.min((width - pad * 2) / dataW, (height - pad * 2) / dataH);
  const offsetX = (width - dataW * scale) / 2 - minX * scale;
  const offsetY = (height - dataH * scale) / 2 - minY * scale;

  const tx = (x) => x * scale + offsetX;
  const ty = (y) => y * scale + offsetY;

  // SVG zusammenbauen
  let svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">
  <defs>
    <style>
      .graph-label { font-family: Inter, system-ui, sans-serif; font-size: 11px; fill: #334155; }
      .graph-label-bold { font-family: Inter, system-ui, sans-serif; font-size: 13px; font-weight: bold; fill: #1e293b; }
      .graph-watermark { font-family: Inter, system-ui, sans-serif; font-size: 10px; fill: #94a3b8; }
      .graph-title { font-family: Inter, system-ui, sans-serif; font-size: 16px; font-weight: bold; fill: #1e293b; }
      .link-label { font-family: Inter, system-ui, sans-serif; font-size: 9px; fill: #64748b; }
    </style>
  </defs>

  <!-- Hintergrund -->
  <rect width="${width}" height="${height}" fill="#fafbfc" rx="8"/>

  <!-- Titel -->
  <text x="20" y="28" class="graph-title">${escapeXml(title)}</text>

  <!-- Legende -->
  <g transform="translate(20, 44)">
    <circle cx="6" cy="6" r="5" fill="#3B82F6"/>
    <text x="16" y="10" class="graph-label">Betreuter</text>
    <circle cx="86" cy="6" r="5" fill="#14B8A6"/>
    <text x="96" y="10" class="graph-label">Institution</text>
    <circle cx="176" cy="6" r="5" fill="#A855F7"/>
    <text x="186" y="10" class="graph-label">Person</text>
  </g>

  <!-- Links -->
  <g class="links">`;

  // Links zeichnen
  for (const link of graphData.links) {
    const source = typeof link.source === 'object' ? link.source : graphData.nodes.find(n => n.id === link.source);
    const target = typeof link.target === 'object' ? link.target : graphData.nodes.find(n => n.id === link.target);
    if (!source || !target || source.x == null || target.x == null) continue;

    const x1 = tx(source.x);
    const y1 = ty(source.y);
    const x2 = tx(target.x);
    const y2 = ty(target.y);
    const color = link.color || '#9CA3AF';

    svg += `
    <line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="${color}" stroke-width="1.5" stroke-opacity="0.6"/>`;

    // Link-Label
    if (link.label) {
      const mx = ((x1 + x2) / 2).toFixed(1);
      const my = ((y1 + y2) / 2).toFixed(1);
      svg += `
    <text x="${mx}" y="${my}" class="link-label" text-anchor="middle" dy="-4">${escapeXml(link.label)}</text>`;
    }
  }

  svg += `
  </g>

  <!-- Nodes -->
  <g class="nodes">`;

  // Nodes zeichnen
  for (const node of graphData.nodes) {
    if (node.x == null || node.y == null) continue;
    const cx = tx(node.x);
    const cy = ty(node.y);
    const r = (node.val || 10) * scale * 0.15;
    const nodeR = Math.max(r, 6);
    const color = node.color || '#666';
    const isKlient = node.typ === 'klient';
    const labelClass = isKlient ? 'graph-label-bold' : 'graph-label';

    // Glow für Klient
    if (isKlient) {
      svg += `
    <circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${(nodeR + 6).toFixed(1)}" fill="${color}" fill-opacity="0.1"/>`;
    }

    svg += `
    <circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${nodeR.toFixed(1)}" fill="${color}" stroke="white" stroke-width="2"/>
    <text x="${cx.toFixed(1)}" y="${(cy + nodeR + 14).toFixed(1)}" class="${labelClass}" text-anchor="middle">${escapeXml(node.name)}</text>`;
  }

  svg += `
  </g>

  <!-- Wasserzeichen -->
  <text x="${width - 12}" y="${height - 10}" class="graph-watermark" text-anchor="end">Olympus Betreuungssoftware · ${new Date().toLocaleDateString('de-DE')}</text>
</svg>`;

  // Download
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = `${filename}_${new Date().toISOString().slice(0, 10)}.svg`;
  link.href = url;
  link.click();
  URL.revokeObjectURL(url);
  return true;
}

function escapeXml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
